import type { Styles } from '@adornis/ass/style.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { getFilters } from '@adornis/bh-registration/client/filter.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { subscribeAllCategoriesForInstitution } from '@adornis/buchungsheld-db/api/category.js';
import { config } from '@adornis/buchungsheld-db/config.js';
import { Category } from '@adornis/buchungsheld-db/entities/category.js';
import { ProductFilter } from '@adornis/buchungsheld-db/entities/products.js';
import {
  RegistrationData,
  RegistrationWorkshopData,
} from '@adornis/buchungsheld-db/entities/RegistrationDataBuffer.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import '@adornis/buchungsheld-theme/components/tr-chip.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { switchMap } from 'rxjs';
import '../../../../components/bh-signup-preview.js';
import '../../../../components/x-code-preview.js';

@customElement('bh-page-admin-einrichtung-settings-embedform')
export class BHPageAdminEinrichtungSettingsEmbedform extends BuchungsheldLitElement {
  @property({ attribute: 'institution-id' }) institutionID = new RXController<string>(this, '');

  @state() private readonly _availableWorkshops = new RXController(
    this,
    this.institutionID.observable.pipe(
      switchMap(institutionID => Workshop.getAllWorkshopsForInstitution(institutionID)({ name: 1 })),
    ),
  );
  @state() private _mode: 'einrichtungen' | 'workshops' | 'einzelworkshop' = 'workshops';
  @state() private _showIframe = false;
  @state() private _selectedWorkshop?: Workshop;

  @state() private readonly availableTimeframes = new RXController(
    this,
    this.institutionID.observable.pipe(
      switchMap(institutionID => Timeframe.subscribeTimeframesByInstitution(institutionID)(Timeframe.allFields)),
    ),
  );

  @state() private readonly availableCategories = new RXController(
    this,
    this.institutionID.observable.pipe(
      switchMap(institutionID => subscribeAllCategoriesForInstitution(institutionID)(Category.allFields)),
    ),
  );

  @state() productFilter = new ProductFilter({});

  private get _searchParams() {
    return new URLSearchParams({
      step: this._mode === 'einzelworkshop' ? 'personal' : 'workshop',
      institution: this.institutionID.value ?? '',
      data:
        this._mode === 'einzelworkshop'
          ? JSON.stringify(
              new RegistrationData({
                institutionID: this.institutionID.value ?? '',
                productData: [new RegistrationWorkshopData({ productID: this._selectedWorkshop?._id })],
              }),
            )
          : '',
    }).toString();
  }

  override render() {
    if (!this.institutionID.value) return html` <tr-loader></tr-loader> `;

    return html`
      <tr-flex space="lg">
        <bh-breadcrumb
          .items=${[
            {
              content: this._translation.translate('settings', 'name'),
              href: `/admin/institution-details/${this.institutionID.value}/settings`,
            },
            { content: this._translation.translate('settings', 'embed_form_guide'), active: true },
          ]}
        ></bh-breadcrumb>

        <tr-text>
          Grundsätzlich können Sie verschiedene Ebenen der Anmeldung in Ihre Webseite einbinden. Die Übersicht über alle
          Institutionen, die Übersicht über alle Workshops die innerhalb Ihrer Institution angeboten werden oder direkt
          eine Vorauswahl zu einem bestimmten Workshop Ihrer Institution.
        </tr-text>

        <tr-tabs .selected=${['einrichtungen', 'workshops', 'einzelworkshops'].indexOf(this._mode)}>
          <tr-tab @click=${() => (this._mode = 'einrichtungen')}>
            ${this._translation.translate('settings', 'ov_institution')}
          </tr-tab>
          <tr-tab @click=${() => (this._mode = 'workshops')}>
            ${this._translation.translate('settings', 'ov_workshop')}
          </tr-tab>
          <tr-tab @click=${() => (this._mode = 'einzelworkshop')}>
            ${this._translation.translate('settings', 'ov_single_workshop')}
          </tr-tab>
        </tr-tabs>

        ${this._preview()}

        <!-- workshops -->
        ${this._mode === 'workshops'
          ? html` <tr-flex space="md">
              <tr-h2> ${this._translation.translate('settings', 'embed_as_webcmp')} </tr-h2>

              <tr-text> ${this._translation.translate('settings', 'embed_as_webcmp_exp')} </tr-text>
              <tr-h3 bold> ${this._translation.translate('settings', 'insert_script')} </tr-h3>
              <tr-text> ${this._translation.translate('settings', 'insert_script_desc')} </tr-text>

              ${this.renderProductFilterBar()} ${this._integrateFormular()}
            </tr-flex>`
          : nothing}

        <!-- einzelworkshop -->
        ${this._mode === 'einzelworkshop'
          ? html` <tr-flex space="md">
                <tr-h2> ${this._translation.translate('settings', 'embed_as_webcmp')} </tr-h2>

                <tr-text> ${this._translation.translate('settings', 'embed_as_webcmp_exp')} </tr-text>
              </tr-flex>

              <tr-flex space="sm">
                <tr-h3 bold> ${this._translation.translate('settings', 'insert_script')} </tr-h3>
                <tr-text> ${this._translation.translate('settings', 'insert_script_desc')} </tr-text>

                ${this.renderWorkshopDropdown()}
              </tr-flex>

              ${this._selectedWorkshop
                ? this._integrateFormular()
                : html`
                    <tr-text tone="warning">
                      ${this._translation.translate('settings', 'wrn_no_workshop_selected')}
                    </tr-text>
                  `}`
          : nothing}

        <!-- einrichtungen -->
        ${this._showIframe || this._mode === 'einrichtungen'
          ? html`
              <tr-flex space="md">
                <tr-h2>
                  ${this._mode !== 'einrichtungen' ? 'Alternativ:' : ''}
                  ${this._translation.translate('settings', 'embed_as_iframe')}
                </tr-h2>
                <tr-text>${this._translation.translate('settings', 'embed_as_iframe_desc')}</tr-text>
                ${this._integrateIFrame()}
              </tr-flex>
              <tr-button
                mode="outline"
                @click=${() => TrDialog.alert(this._translation.translate('settings', 'contact_adm_to_embed'))}
              >
                ${this._translation.translate('settings', 'iframe_embed_not_working')}
              </tr-button>
            `
          : html`
              <tr-button mode="outline" @click=${() => (this._showIframe = true)}>
                ${this._translation.translate('settings', 'webcmp_embed_not_working')}
              </tr-button>
            `}
      </tr-flex>
    `;
  }

  private _integrateFormular() {
    const compParams = {} as any;
    const p_add = (key, value) => (compParams[key] = value);
    const pf = this.productFilter;
    if (pf.categoryIDs) p_add('data-filter-categories', pf.categoryIDs.join(','));

    return html`
      <x-code-preview
        small
        .value=${`<!-- stylesheet -->
<link rel="stylesheet" href="${baseConfig.get('ROOT_URL')}__standalone/index.css">

<!-- scripts -->
<script>
  window["adornisConfig_"] = {
    ROOT_URL: "${baseConfig.get('ROOT_URL')}",
    DEPLOYMENT: "${baseConfig.get('DEPLOYMENT')}",
  };
  window["adornisConfig_buchungsheld"] = {
    ROOT_URL: "${baseConfig.get('ROOT_URL')}",
    DEPLOYMENT: "${baseConfig.get('DEPLOYMENT')}",
    LDAP_IMPLEMENTATION: "${config.get('LDAP_IMPLEMENTATION')}",
    SAML_IMPLEMENTATION: "${config.get('SAML_IMPLEMENTATION')}",
  };
  window["adornisConfig_base"] = {
    ROOT_URL: "${baseConfig.get('ROOT_URL')}",
    DEPLOYMENT: "${baseConfig.get('DEPLOYMENT')}",
  };
  window["adornisConfig_file-config"] = {
    "FILES_EXPRESS_BASE_URL": "/api/files"
  }
  window["adornisConfig_adornis_router"] = {
    "USE_VIRTUAL_ROUTING": "true",
    "VIRTUAL_PATH": "/registration",
    "VIRTUAL_SEARCH_PARAMS": "${this._searchParams}",
  }
  window["adornisConfig_whatsapp-config"] = {};
</script>
<script
    type="module"
    src="${baseConfig.get('ROOT_URL')}__standalone/index.bundle.js"
></script>

<!-- webcomponent -->
<bh-embed-registration
  ${Object.entries(compParams)
    .map(([key, value]) => `${key}="${value}"`)
    .join('/n')}
  ></bh-embed-registration>
`}
      >
      </x-code-preview>
    `;
  }

  private _integrateIFrame() {
    return html`
      <x-code-preview
        small
        .value=${`<iframe src="${baseConfig.get('ROOT_URL')}registration?${
          this._searchParams
        }" style="border:none;"></iframe>`}
      ></x-code-preview>
    `;
  }

  private _preview() {
    // TODO @stieber hier preview einfügen, findest du dann auf https://runtime-buchungsheldcareerservice.nexus.adornis.deinstitution-details/65646a689adc34c8f83f7b25/settings/embed-form (TestUser - Test123!)
    switch (this._mode) {
      case 'einrichtungen':
        return html` <bh-signup-preview mode="institutions"></bh-signup-preview> `;
      case 'workshops':
        return html` <bh-signup-preview mode="workshops"></bh-signup-preview> `;
      case 'einzelworkshop':
        return html` <bh-signup-preview mode="single"></bh-signup-preview> `;
    }
  }

  private renderProductFilterBar() {
    return html`
      <!-- workshops -->
      <bh-filter-bar
        .allFilters=${(() => {
          return getFilters({
            filter: this.productFilter,
            translation: this._translation,
            availableTimeframes: this.availableTimeframes.value ?? [],
            availableCategories: this.availableCategories.value ?? [],
            productTopics: [],
          });
        })()}
        @onSave=${e => {
          this.productFilter[e.detail.key] = e.detail.value;
          this.requestUpdate();
        }}
      ></bh-filter-bar>
    `;
  }

  renderWorkshopDropdown() {
    return html`
      <!-- einzelworkshop -->
      <tr-flex space="sm">
        <tr-text> ${this._translation.translate('settings', 'embed_tut_1')} </tr-text>
        <tr-text tone="subtle"> ${this._translation.translate('settings', 'embed_tut_2')} </tr-text>

        <x-dropdown-search-selection
          .emptyState=${this._translation.translate('dropdowns', 'emptyStateWorkshops')}
          placeholder="Workshop auswählen"
          @value-picked=${(e: ValueEvent<Workshop>) => (this._selectedWorkshop = e.detail.value)}
          .search=${async search => {
            const workshops = this._availableWorkshops.value;
            if (!workshops) return [];
            return workshops.filter(workshop => {
              const wkName = workshop.name.toLowerCase();
              return wkName.includes(search.toLowerCase());
            });
          }}
          .renderString=${doc => doc?.name ?? ''}
          clearable
        ></x-dropdown-search-selection>
      </tr-flex>
    `;
  }

  override styles() {
    return [
      ...super.styles(),
      {
        'tr-text': { maxWidth: this.sizes.maxReadableWidth, lineHeight: `CALC( ${this.spacing.md} + 2px )` },
        '[placeholder-wrapper]': {
          '[container]': {
            border: `2px solid ${this.colors.neutral.shade(200)}`,
            borderRadius: this.spacing.xs,
            overflow: 'hidden',

            '[content]': {
              '[cover]': {
                height: '200px',
                aspectRatio: '1 / 1',
              },
              '[text]': {
                width: '100%',
                maxWidth: 'none',
              },
              'tr-chip': { borderRadius: this.spacing.md },
            },
          },
        },
      },
    ] as Styles[];
  }
}
